// Display helpers for portfolio numbers. API sends decimals as strings (docs/portfolios/SPEC.md, decision 12).
import { intlLocale, t } from './i18n';

export function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatNumber(value, maxFractionDigits = 4) {
  const n = toNumber(value);
  if (n === null) {
    return '—';
  }
  return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: maxFractionDigits }).format(n);
}

export function formatMoney(value, currency) {
  const n = toNumber(value);
  if (n === null) {
    return '—';
  }
  const fractionDigits = Math.abs(n) < 1 && n !== 0 ? 4 : 2;
  try {
    // One minus sign everywhere: U+2212, as in formatSignedPercent (docs/design/SPEC.md).
    return new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency: currency || 'RUB',
      minimumFractionDigits: 2,
      maximumFractionDigits: fractionDigits,
    }).format(n).replace(/^-/, '−');
  } catch {
    // Unknown currency code from the API
    return `${formatNumber(n, fractionDigits)} ${currency ?? ''}`.trim();
  }
}

// Big sums in short form: "12,3 млрд ₽", "6,3 трлн ₽" (heatmap turnover and capitalization).
export function formatCompactMoney(value, currency = 'RUB') {
  const n = toNumber(value);
  if (n === null) {
    return '—';
  }
  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(n);
  } catch {
    return `${formatNumber(n, 0)} ${currency}`;
  }
}

export function formatSignedMoney(value, currency) {
  const n = toNumber(value);
  if (n === null) {
    return '—';
  }
  return (n > 0 ? '+' : '') + formatMoney(n, currency);
}

export function formatPercent(value) {
  const n = toNumber(value);
  if (n === null) {
    return '—';
  }
  return `${n > 0 ? '+' : ''}${new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 2 }).format(n)} %`;
}

// Map currency → amount as "12 400,00 ₽ + 35,00 $": RUB first, zero sums skipped, nothing → "—".
export function formatCurrencySums(sums) {
  const entries = [...sums.entries()].filter(([, amount]) => amount !== 0);
  if (entries.length === 0) {
    return '—';
  }
  entries.sort(([a], [b]) => (a === 'RUB' ? -1 : b === 'RUB' ? 1 : a.localeCompare(b)));
  return entries.map(([currency, amount]) => formatMoney(amount, currency)).join(' + ');
}

// Percent with an explicit sign (+ / U+2212 minus) and one decimal: the sign must not rely on color
// alone (docs/design/SPEC.md, decision 5).
export function formatSignedPercent(value, digits = 1) {
  const n = toNumber(value);
  if (n === null) {
    return '—';
  }
  const text = new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Math.abs(n));
  // A value that rounds to zero has no sign: "0,0 %", not "−0,0 %".
  const sign = Number(Math.abs(n).toFixed(digits)) === 0 ? '' : n > 0 ? '+' : '−';
  return `${sign}${text} %`;
}

// Percent without a sign: the color shows it (docs/portfolios/SPEC.md, decision 15).
export function formatUnsignedPercent(value) {
  const n = toNumber(value);
  return n === null ? '—' : formatPercent(Math.abs(n)).replace(/^\+/, '');
}

export function signClass(value) {
  const n = toNumber(value);
  if (n === null || n === 0) {
    return 'text-slate-400';
  }
  return n > 0 ? 'text-emerald-400' : 'text-rose-400';
}

export function formatDateTime(iso) {
  if (!iso) {
    return '—';
  }
  return new Date(iso).toLocaleString(intlLocale(), { dateStyle: 'short', timeStyle: 'short' });
}

export function formatTime(iso) {
  if (!iso) {
    return '—';
  }
  return new Date(iso).toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const ACCOUNT_TYPES = [
  'ACCOUNT_TYPE_TINKOFF',
  'ACCOUNT_TYPE_TINKOFF_IIS',
  'ACCOUNT_TYPE_INVEST_BOX',
  'ACCOUNT_TYPE_INVEST_FUND',
  'ACCOUNT_TYPE_DEBIT',
  'ACCOUNT_TYPE_SAVING',
];

export function accountTypeLabel(type) {
  return ACCOUNT_TYPES.includes(type) ? t(`accountType.${type}`) : t('accountType.other');
}

const INSTRUMENT_TYPES = ['share', 'bond', 'etf', 'currency', 'futures', 'option', 'sp'];

export function instrumentTypeLabel(type) {
  return INSTRUMENT_TYPES.includes(type) ? t(`instrumentType.${type}`) : t('instrumentType.other');
}
